	import { el } from "./dom.js";
	import { api } from "../net/api.js";
	import { CharacterPreview } from "../game/previews.js";
	import { SKIN_REGIONS } from "../game/skinLayout.js";
	import type { Cosmetics } from "@neon-purge/shared";

const SIZE = 256;
const DISPLAY = 300;
const PALETTE = [
  "#00f0ff", "#ff2ec4", "#9dff00", "#ffb300", "#ff3355", "#8a2eff",
  "#ffffff", "#000000", "#2a2f3d", "#c23a2a", "#37d67a", "#3fa9f5",
];

interface Sticker { img: HTMLImageElement; x: number; y: number; scale: number; }

/**
 * Layered Skin Lab: a painted base layer + stackable image "stickers" you can
 * drag to move and scale. Save several named skins to your library and pick
 * which one to equip. Full-body atlas (head/torso/arms/legs) so nothing
 * stretches; live 3D preview.
 */
export function skinEditor(onBack: () => void): HTMLElement {
  const screen = el("div", { class: "screen cos-screen skinlab" });
  const status = el("div", { class: "error" });

  // display canvas (shows base + stickers), and an offscreen base paint layer
  const canvas = document.createElement("canvas");
  canvas.width = SIZE; canvas.height = SIZE;
  canvas.className = "skin-canvas";
  canvas.style.width = `${DISPLAY}px`; canvas.style.height = `${DISPLAY}px`;
  const ctx = canvas.getContext("2d")!;
  const base = document.createElement("canvas"); base.width = base.height = SIZE;
  const bctx = base.getContext("2d")!;

  const SHADES: Record<string, string> = { head: "#3a3f4d", torso: "#2f3442", armL: "#2a2f3d", armR: "#2a2f3d", legL: "#242938", legR: "#242938" };
  const seedBands = (): void => {
    bctx.globalCompositeOperation = "source-over";
    bctx.fillStyle = "#0d1018"; bctx.fillRect(0, 0, SIZE, SIZE);
    for (const [k, r] of Object.entries(SKIN_REGIONS)) { bctx.fillStyle = SHADES[k] ?? "#2a2f3d"; bctx.fillRect(r.x * SIZE, r.y * SIZE, r.w * SIZE, r.h * SIZE); }
  };
  seedBands();

  const stickers: Sticker[] = [];
  let selected = -1;
  let mode: "paint" | "move" = "paint";

  // live preview
  const previewCanvas = document.createElement("canvas"); previewCanvas.className = "char3d";
  let preview: CharacterPreview | null = null;
	  const cos: Partial<Cosmetics> = api.account?.cosmetics ?? {};

  const compose = (target: CanvasRenderingContext2D): void => {
    target.clearRect(0, 0, SIZE, SIZE);
    target.drawImage(base, 0, 0);
    for (const s of stickers) {
      const w = s.img.width * s.scale, h = s.img.height * s.scale;
      target.drawImage(s.img, s.x - w / 2, s.y - h / 2, w, h);
    }
  };
  const composedURL = (): string => { const c = document.createElement("canvas"); c.width = c.height = SIZE; compose(c.getContext("2d")!); return c.toDataURL("image/png"); };
  const syncPreview = (): void => preview?.setCosmetics({ bodySkin: cos.bodySkin, glowColor: cos.glowColor, headgear: cos.headgear, skinUrl: composedURL() });

  const redraw = (): void => {
    compose(ctx);
    if (selected >= 0 && stickers[selected]) {
      const s = stickers[selected];
      const w = s.img.width * s.scale, h = s.img.height * s.scale;
      ctx.strokeStyle = "#00f0ff"; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
      ctx.strokeRect(s.x - w / 2, s.y - h / 2, w, h); ctx.setLineDash([]);
    }
  };

  // --- tools ---
  let color = "#00f0ff", brush = 10, erasing = false, drawing = false, dragging = false;
  const colorInput = el("input", { type: "color", value: color, style: "width:44px;height:34px;padding:2px" }) as HTMLInputElement;
  colorInput.oninput = () => { color = colorInput.value; setErase(false); };
  const palette = el("div", { class: "palette" }, ...PALETTE.map(() => el("button", { class: "pal" })));
  [...palette.children].forEach((b, i) => { (b as HTMLElement).style.setProperty("--sw", PALETTE[i]); (b as HTMLElement).onclick = () => { color = PALETTE[i]; colorInput.value = PALETTE[i]; setErase(false); }; });
  const brushInput = el("input", { type: "range", min: "2", max: "40", value: String(brush), class: "slider" }) as HTMLInputElement;
  brushInput.oninput = () => (brush = Number(brushInput.value));
  const eraseBtn = el("button", { class: "ghost", text: "Eraser" });
  const setErase = (v: boolean): void => { erasing = v; eraseBtn.classList.toggle("active", v); };
  eraseBtn.onclick = () => setErase(!erasing);

  // mode toggle: paint vs move stickers
  const paintBtn = el("button", { class: "seg active", text: "🖌 Paint" });
  const moveBtn = el("button", { class: "seg", text: "✥ Move image" });
  const setMode = (m: "paint" | "move"): void => { mode = m; paintBtn.classList.toggle("active", m === "paint"); moveBtn.classList.toggle("active", m === "move"); if (m === "paint") { selected = -1; redraw(); } };
  paintBtn.onclick = () => setMode("paint");
  moveBtn.onclick = () => setMode("move");

  // selected-sticker controls
  const baseScale = new WeakMap<Sticker, number>();
  const scaleInput = el("input", { type: "range", min: "10", max: "400", value: "100", class: "slider" }) as HTMLInputElement;
  scaleInput.oninput = () => { if (selected >= 0) { const st = stickers[selected]; st.scale = (Number(scaleInput.value) / 100) * (baseScale.get(st) ?? 1); redraw(); syncPreview(); } };
  const delStickerBtn = el("button", { class: "ghost", text: "🗑 Delete image" });
  delStickerBtn.onclick = () => { if (selected >= 0) { stickers.splice(selected, 1); selected = -1; redraw(); syncPreview(); } };

  const toCanvas = (e: PointerEvent): [number, number] => { const r = canvas.getBoundingClientRect(); return [((e.clientX - r.left) / r.width) * SIZE, ((e.clientY - r.top) / r.height) * SIZE]; };
  const paintAt = (x: number, y: number): void => { bctx.globalCompositeOperation = erasing ? "destination-out" : "source-over"; bctx.fillStyle = color; bctx.beginPath(); bctx.arc(x, y, brush / 2, 0, Math.PI * 2); bctx.fill(); bctx.globalCompositeOperation = "source-over"; };
  const hitSticker = (x: number, y: number): number => { for (let i = stickers.length - 1; i >= 0; i--) { const s = stickers[i]; const w = s.img.width * s.scale, h = s.img.height * s.scale; if (x >= s.x - w / 2 && x <= s.x + w / 2 && y >= s.y - h / 2 && y <= s.y + h / 2) return i; } return -1; };

  canvas.addEventListener("pointerdown", (e) => {
    canvas.setPointerCapture(e.pointerId);
    const [x, y] = toCanvas(e);
    if (mode === "move") {
      selected = hitSticker(x, y);
      if (selected >= 0) { dragging = true; scaleInput.value = String(Math.round((stickers[selected].scale / (baseScale.get(stickers[selected]) ?? 1)) * 100)); }
      redraw();
    } else { drawing = true; paintAt(x, y); redraw(); }
  });
  canvas.addEventListener("pointermove", (e) => {
    const [x, y] = toCanvas(e);
    if (mode === "move" && dragging && selected >= 0) { stickers[selected].x = x; stickers[selected].y = y; redraw(); }
    else if (mode === "paint" && drawing) { paintAt(x, y); redraw(); }
  });
  canvas.addEventListener("pointerup", () => { drawing = false; dragging = false; syncPreview(); });

  const fileInput = el("input", { type: "file", accept: "image/*", style: "display:none" }) as HTMLInputElement;
  fileInput.onchange = () => {
    const file = fileInput.files?.[0]; if (!file) return;
    const img = new Image();
    img.onload = () => {
      const fit = Math.min((SIZE * 0.5) / img.width, (SIZE * 0.5) / img.height); // start ~half-canvas
      const st: Sticker = { img, x: SIZE / 2, y: SIZE / 2, scale: fit };
      baseScale.set(st, fit);
      stickers.push(st); selected = stickers.length - 1; setMode("move");
      scaleInput.value = "100"; redraw(); syncPreview();
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  };
  const uploadBtn = el("button", { class: "ghost", text: "🖼 Add Picture" });
  uploadBtn.onclick = () => fileInput.click();
  const fillBtn = el("button", { class: "ghost", text: "Fill" });
  fillBtn.onclick = () => { bctx.globalCompositeOperation = "source-over"; bctx.fillStyle = color; bctx.fillRect(0, 0, SIZE, SIZE); redraw(); syncPreview(); };
  const clearBtn = el("button", { class: "ghost", text: "Clear" });
  clearBtn.onclick = () => { seedBands(); stickers.length = 0; selected = -1; redraw(); syncPreview(); };

  // --- save / gallery ---
  const nameInput = el("input", { type: "text", placeholder: "skin name", maxlength: "40", value: "My Skin" }) as HTMLInputElement;
  const gallery = el("div", { class: "skin-gallery" });
  const refreshGallery = async (): Promise<void> => {
    try {
      const skins = await api.listSkins();
      gallery.replaceChildren(...(skins.length === 0 ? [el("div", { class: "hint", text: "no saved skins yet" })] : skins.map((sk) => {
        const thumb = el("div", { class: "skin-thumb", title: sk.name });
        thumb.style.backgroundImage = `url(${sk.data})`;
        const nm = el("div", { class: "skin-thumb-name", text: sk.name });
        const equip = el("button", { class: "mini", text: "equip" });
        equip.onclick = async () => { await api.equipSkin(sk.id); loadInto(sk.data); status.style.color = "var(--cyan)"; status.textContent = `equipped "${sk.name}"`; };
        const load = el("button", { class: "mini", text: "edit" });
        load.onclick = () => loadInto(sk.data);
        const del = el("button", { class: "mini danger", text: "✕" });
        del.onclick = async () => { await api.deleteSkin(sk.id); void refreshGallery(); };
        return el("div", { class: "skin-slot" }, thumb, nm, el("div", { class: "row" }, equip, load, del));
      })));
    } catch (e) { gallery.replaceChildren(el("div", { class: "error", text: (e as Error).message })); }
  };
  const loadInto = (url: string): void => {
    const img = new Image();
    img.onload = () => { bctx.globalCompositeOperation = "source-over"; bctx.clearRect(0, 0, SIZE, SIZE); bctx.drawImage(img, 0, 0, SIZE, SIZE); stickers.length = 0; selected = -1; setMode("paint"); redraw(); syncPreview(); };
    img.src = url;
  };

  const save = el("button", { class: "primary wide", text: "💾 Save skin" });
  save.onclick = async () => {
    save.disabled = true; status.textContent = "";
    try {
      let url = composedURL();
      if (url.length > 90 * 1024) { const c = document.createElement("canvas"); c.width = c.height = SIZE; compose(c.getContext("2d")!); url = c.toDataURL("image/jpeg", 0.85); }
      const name = nameInput.value.trim() || "My Skin";
      await api.saveSkin(name, url); // add to library
      await api.patchSkin(url);      // equip immediately
      status.style.color = "var(--cyan)"; status.textContent = `saved & equipped "${name}"`;
      void refreshGallery();
    } catch (err) { status.style.color = "var(--danger)"; status.textContent = (err as Error).message; }
    finally { save.disabled = false; }
  };
  const removeBtn = el("button", { class: "magenta wide", text: "Unequip skin" });
  removeBtn.onclick = async () => { try { await api.patchSkin(null); status.style.color = "var(--dim)"; status.textContent = "skin unequipped"; } catch (e) { status.textContent = (e as Error).message; } };

  const back = el("button", { class: "ghost wide", text: "Back" });
  back.onclick = () => { preview?.dispose(); onBack(); };

  screen.append(
    el("div", { class: "cos-head" }, el("div", { class: "brand small", text: "SKIN LAB" }), el("div", { class: "brand-sub", text: "DRAW · STACK IMAGES · SAVE" })),
    el("div", { class: "cos-grid" },
      el("div", { class: "card" },
        el("div", { class: "card-title", text: "CANVAS — full-body atlas" }),
        el("div", { style: "display:flex;justify-content:center" },
          el("div", { class: "skin-wrap" }, canvas,
            el("div", { class: "skin-guide" }, ...Object.values(SKIN_REGIONS).map((r) =>
              el("div", { class: "zone-box", style: `left:${r.x * 100}%;top:${r.y * 100}%;width:${r.w * 100}%;height:${r.h * 100}%` }, el("span", { text: r.label })))))),
        el("div", { class: "sub" },
          el("div", { class: "seg-row" }, paintBtn, moveBtn),
          el("div", { class: "row mt1", style: "align-items:flex-end;gap:0.6rem" }, el("div", {}, el("label", { text: "Color" }), colorInput), el("div", { style: "flex:1" }, el("label", { text: "Brush" }), brushInput), eraseBtn),
          palette,
          el("div", { class: "row mt1" }, fillBtn, uploadBtn, clearBtn), fileInput,
          el("label", { text: "Selected image — scale", style: "margin-top:0.8rem" }),
          el("div", { class: "row", style: "align-items:center;gap:0.6rem" }, scaleInput, delStickerBtn))),
      el("div", { class: "card char-card" },
        el("div", { class: "card-title", text: "LIVE PREVIEW" }),
        el("div", { class: "char3d-wrap" }, previewCanvas, el("div", { class: "map3d-hint", text: "drag to rotate" })),
        el("label", { text: "Skin name", style: "margin-top:0.6rem" }), nameInput,
        el("div", { class: "row mt1" }, save, removeBtn), status,
        el("div", { class: "card-title", text: "MY SKINS", style: "margin-top:1rem" }), gallery)),
    back,
  );

  requestAnimationFrame(() => {
    if (!screen.isConnected) return;
    preview = new CharacterPreview(previewCanvas, { bodySkin: cos.bodySkin, glowColor: cos.glowColor, headgear: cos.headgear, skinUrl: api.account?.skinUrl });
    preview.start();
    redraw();
    void refreshGallery();
    // seed from the currently equipped skin
    if (api.account?.skinUrl) loadInto(api.account.skinUrl);
  });

  return screen;
}
